<!-- One unit per attachment: the row with the type icon and the name comes
     first, a video or a recording adds its player underneath, inside the
     same card, so a player can never sit between two names. -->
<div
  class="attachment"
  [class.attachment--card]="card || playsInline"
>
  <ion-item
    lines="none"
    [class]="withPadding ? 'with-padding' : 'ion-no-padding'"
    detail="false"
  >
    <a
      class="document-link"
      (click)="open()"
      [attr.aria-label]="openLabel | translate: { title: item.title }"
      [title]="openLabel | translate: { title: item.title }"
    >
      <span class="document-icon">
        <!-- A list's media: a video's first frame with a play mark on top, a
             recording a tinted tile with the play mark alone. -->
        <span
          *ngIf="playsInModal; else typeIcon"
          class="media-thumb"
          [class.media-thumb--audio]="isAudio"
          data-cy="media-thumb"
        >
          <video
            *ngIf="isVideo && !thumbFailed"
            class="media-thumb__frame"
            muted
            playsinline
            preload="metadata"
            tabindex="-1"
            aria-hidden="true"
            [src]="thumbnailUrl"
            (error)="onThumbError()"
          ></video>
          <fl-document-image
            *ngIf="thumbFailed"
            [document]="item"
          ></fl-document-image>
          <span
            class="media-thumb__play"
            aria-hidden="true"
          >
            <ion-icon name="play"></ion-icon>
          </span>
        </span>
        <ng-template #typeIcon>
          <fl-document-image [document]="item"></fl-document-image>
        </ng-template>
        <span
          *ngIf="folderName"
          class="shortcut-badge"
          aria-hidden="true"
        >
          <svg-icon
            key="arrow-up-right"
            [fontSize]="12"
          ></svg-icon>
        </span>
      </span>
      <span class="document-title ml-5">
        <span class="font--base">{{ item.title }}</span>
        <span
          *ngIf="folderName"
          class="folder-caption"
        >
          <svg-icon
            key="folder"
            [fontSize]="13"
            aria-hidden="true"
          ></svg-icon>
          {{ folderName }}
        </span>
        <span
          *ngIf="item.updated_at && showUpdatedAt"
          class="row-subtitle"
        >
          {{ item.updated_at | relativeTime }}
        </span>
      </span>
    </a>
    <div
      slot="end"
      class="row-end"
    >
      <div
        *ngIf="item.updated_at && showUpdatedAt"
        class="updated-at-col"
        [title]="item.updated_at | date: 'dd.MM.yyyy HH:mm'"
      >
        {{ item.updated_at | relativeTime }}
      </div>
      <div
        *ngIf="item.updated_at && showUpdatedAt"
        class="row-divider"
        aria-hidden="true"
      ></div>
      <div class="document-actions">
        <ion-button
          *ngIf="showLinkAction && hasDocumentPage"
          fill="clear"
          size="small"
          (click)="shareLink()"
          [attr.aria-label]="'COMMON.LABELS.SHARE' | translate"
          [title]="'COMMON.LABELS.SHARE' | translate"
          data-cy="document-link-action"
        >
          <svg-icon
            slot="icon-only"
            key="share"
            class="color--darkblue"
            [fontSize]="18"
            aria-hidden="true"
          ></svg-icon>
        </ion-button>
        <ion-button
          *ngIf="showOptions && isAdmin"
          fill="clear"
          size="small"
          (click)="promptRename()"
          [attr.aria-label]="'COMMON.LABELS.RENAME' | translate"
          [title]="'COMMON.LABELS.RENAME' | translate"
        >
          <svg-icon
            slot="icon-only"
            key="pencil-line"
            class="color--darkblue"
            [fontSize]="18"
            aria-hidden="true"
          ></svg-icon>
        </ion-button>
        <ion-button
          *ngIf="showOptions && isAdmin && showMoveAction"
          fill="clear"
          size="small"
          (click)="promptMove()"
          [attr.aria-label]="'COMMON.LABELS.MOVE' | translate"
          [title]="'COMMON.LABELS.MOVE' | translate"
          data-cy="document-move-action"
        >
          <svg-icon
            slot="icon-only"
            key="arrow-up-right"
            class="color--darkblue"
            [fontSize]="18"
            aria-hidden="true"
          ></svg-icon>
        </ion-button>
        <ion-button
          *ngIf="showOptions"
          fill="clear"
          size="small"
          (click)="confirmAndDelete()"
          [attr.aria-label]="'COMMON.LABELS.DELETE' | translate"
          [title]="'COMMON.LABELS.DELETE' | translate"
        >
          <svg-icon
            slot="icon-only"
            key="trash"
            class="color--danger"
            [fontSize]="18"
            aria-hidden="true"
          ></svg-icon>
        </ion-button>
      </div>
    </div>
  </ion-item>
  <div
    *ngIf="playsInline"
    class="media-body"
  >
    <div
      *ngIf="isVideo && mediaPlayable"
      class="video-frame"
    >
      <video
        class="media-player"
        controls
        preload="metadata"
        playsinline
        [src]="item.url"
        (error)="onMediaError($event)"
        (loadedmetadata)="onMediaReady()"
        [attr.aria-label]="
          'COMPONENTS.MEDIA.PLAY_VIDEO' | translate: { title: item.title }
        "
      ></video>
      <div
        *ngIf="!mediaReady"
        class="video-placeholder"
        aria-hidden="true"
        data-cy="video-placeholder"
      >
        <svg-icon
          key="video"
          size="lg"
        ></svg-icon>
        <span>{{ item.title }}</span>
      </div>
    </div>
    <audio
      *ngIf="isAudio && mediaPlayable"
      class="media-player"
      controls
      preload="metadata"
      [src]="item.url"
      (error)="onMediaError($event)"
      [attr.aria-label]="
        'COMPONENTS.MEDIA.PLAY_AUDIO' | translate: { title: item.title }
      "
    ></audio>
    <!-- The signed url lives an hour, so a page left open longer plays into a
         403. That first failure renews it without asking; this box only holds
         the card's height while the record is re-read. -->
    <div
      *ngIf="mediaReloading"
      class="media-status media-status--busy"
      data-cy="media-reloading"
    >
      <ion-spinner
        name="dots"
        aria-hidden="true"
      ></ion-spinner>
    </div>
    <div
      *ngIf="mediaUnavailable"
      class="media-status"
      data-cy="media-unavailable"
    >
      <span>{{ "COMPONENTS.MEDIA.UNAVAILABLE" | translate }}</span>
    </div>
    <!-- The fresh url failed too, so it is the file the browser cannot decode
         (an iPhone .mov, say). Nothing left to retry - offer the file itself. -->
    <div
      *ngIf="mediaUnplayable"
      class="media-status"
      data-cy="media-unplayable"
    >
      <span>{{ "COMPONENTS.MEDIA.UNPLAYABLE" | translate }}</span>
      <ion-button
        fill="clear"
        size="small"
        (click)="downloadDocument()"
        data-cy="media-download"
      >
        {{ "COMMON.LABELS.DOWNLOAD" | translate }}
      </ion-button>
    </div>
  </div>
</div>
